import { useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { getApiIntegrationLoading, getHasApiIntegration } from 'src/selectors/customerProduct'
import { getWebhookSending, getWebhookSubscriptions } from 'src/selectors/webhook'
import { hasActiveSubscriptionForEvent } from 'src/utils/webhookSubscriptionList'
import type { SendTestWebhookFormValues } from './SendTestWebhookModal.interface'

interface SendTestWebhookGate {
  /** Why the backend would reject the send right now; `null` when it would not. */
  blockedReason: string | null
  /** No event chosen yet, or `blockedReason` set — the submit stays disabled. */
  isBlocked: boolean
  isSending: boolean
}

/**
 * The two reasons the backend rejects a test send, both as 400s, recomputed as the admin changes
 * the event select. Read from Formik, so it works anywhere inside the form's provider — the
 * fields (which print the reason) and the modal footer's submit button (which it disables) both
 * use it.
 */
export const useSendTestWebhookGate = (): SendTestWebhookGate => {
  const { values } = useFormikContext<SendTestWebhookFormValues>()

  const subscriptions = useSelector(getWebhookSubscriptions)
  const hasApiIntegration = useSelector(getHasApiIntegration)
  const isGateLoading = useSelector(getApiIntegrationLoading)
  const isSending = useSelector(getWebhookSending)

  const coversChosenEvent = useMemo(() => {
    return hasActiveSubscriptionForEvent(subscriptions, values.eventType || null)
  }, [subscriptions, values.eventType])

  const blockedReason = useMemo(() => {
    if (isGateLoading) {
      return 'Checking whether this customer is enabled for API integration…'
    }

    if (!hasApiIntegration) {
      return 'This customer does not have the API integration product enabled, so GigSafe will reject the send. Enable it first, or check that the access check succeeded.'
    }

    if (values.eventType === '') {
      return null
    }

    if (!coversChosenEvent) {
      return 'No active subscription lists this event, so GigSafe will reject the send. Add the event to a subscription, or enable one that already has it.'
    }

    return null
  }, [isGateLoading, hasApiIntegration, values.eventType, coversChosenEvent])

  return {
    blockedReason,
    isBlocked: blockedReason !== null || values.eventType === '',
    isSending,
  }
}
